import {rdvActivationKeysStore} from "@mytour/company-settings/rdvActivationQueries";
import {invalidatePageQueries} from "@mytour/platform/invalidatePageQueries";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {serviceDescriptionApiService} from "./serviceDescriptionApi.service.js";

export const serviceDescriptionKeysStore = {
  root: ["serviceDescription"] as const,
  currentServiceDescriptions: () => [...serviceDescriptionKeysStore.root, "current"] as const,
  serviceDescriptionPage: () =>
    [...serviceDescriptionKeysStore.currentServiceDescriptions(), "page"] as const,
  allServiceDescriptions: () => [...serviceDescriptionKeysStore.root, "all"] as const,
  serviceDescription: (id: string) => [...serviceDescriptionKeysStore.root, "detail", id] as const,
};

export function useServiceDescriptionPage() {
  const {data, isLoading, isFetching, isError, refetch} = useQuery({
    queryKey: serviceDescriptionKeysStore.serviceDescriptionPage(),
    queryFn: serviceDescriptionApiService.getServiceDescriptionPage,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
  return {
    serviceDescriptions: data?.data,
    serviceDescriptionsPageMeta: data?.meta,
    isServiceDescriptionsLoading: isLoading,
    isServiceDescriptionsFetching: isFetching,
    isServiceDescriptionsError: isError,
    refetchServiceDescriptions: refetch,
  };
}

/**
 * Hook to get current user's service descriptions
 * 📋 Fetches the service descriptions associated with the current user
 */
export function useServiceDescriptions() {
  const {data, isLoading, isFetching, isError, refetch} = useQuery({
    queryKey: serviceDescriptionKeysStore.currentServiceDescriptions(),
    queryFn: serviceDescriptionApiService.getCurrentUserServiceDescriptions,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    serviceDescriptions: data,
    isServiceDescriptionsLoading: isLoading,
    isServiceDescriptionsError: isError,
    isServiceDescriptionsFetching: isFetching,
    refetchServiceDescriptions: refetch,
  };
}

/**
 * Hook to get a single service description by ID
 * 🔍 Fetches detailed information for a specific service description
 */
export function useServiceDescription(id: string | undefined) {
  const {data, isLoading, isFetching, error, refetch} = useQuery({
    queryKey: serviceDescriptionKeysStore.serviceDescription(id as string),
    queryFn: () => serviceDescriptionApiService.getServiceDescription(id as string),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    serviceDescription: data,
    isServiceDescriptionLoading: isLoading,
    isServiceDescriptionFetching: isFetching,
    serviceDescriptionError: error,
    refetchServiceDescription: refetch,
  };
}

/**
 * Hook to create a new service description
 * ➕ Creates a new service description and invalidates related queries
 */
export function useCreateServiceDescription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: serviceDescriptionApiService.createServiceDescription,
    async onSuccess() {
      await invalidatePageQueries(
        queryClient,
        serviceDescriptionKeysStore.serviceDescriptionPage()
      );
      queryClient.invalidateQueries({queryKey: serviceDescriptionKeysStore.root});

      // A service's online-bookability (onlineBookingEnabled + duration) is an RDV
      // activation prerequisite (Story 5.1, #607): refresh the activation status so the
      // switch/missing list reflects the new service without waiting for the staleTime.
      queryClient.invalidateQueries({queryKey: rdvActivationKeysStore.status()});
    },
    onError(error) {
      console.error("Failed to create service description:", error);
    },
  });
}

/**
 * Hook to update a service description
 * ✏️ Updates an existing service description and invalidates related queries
 */
export function useUpdateServiceDescription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: serviceDescriptionApiService.updateServiceDescription,
    async onSuccess(_updatedServiceDescription, input) {
      queryClient.invalidateQueries({
        queryKey: serviceDescriptionKeysStore.serviceDescription(input.id as string),
      });

      await invalidatePageQueries(
        queryClient,
        serviceDescriptionKeysStore.serviceDescriptionPage()
      );
      // Invalidate list queries to ensure consistency
      queryClient.invalidateQueries({
        queryKey: serviceDescriptionKeysStore.allServiceDescriptions(),
      });
      queryClient.invalidateQueries({
        queryKey: serviceDescriptionKeysStore.currentServiceDescriptions(),
      });

      // A service's online-bookability (onlineBookingEnabled + duration) is an RDV
      // activation prerequisite (Story 5.1, #607): refresh the activation status so the
      // switch/missing list reflects the edit without waiting for the staleTime.
      queryClient.invalidateQueries({queryKey: rdvActivationKeysStore.status()});
    },
    onError(error) {
      console.error("Failed to update service description:", error);
    },
  });
}

/**
 * Hook to delete a service description
 * 🗑️ Deletes a service description and invalidates related queries
 */
export function useDeleteServiceDescription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: serviceDescriptionApiService.deleteServiceDescription,
    async onSuccess(_, deletedId) {
      // Remove the service description from the cache
      queryClient.removeQueries({
        queryKey: serviceDescriptionKeysStore.serviceDescription(deletedId),
      });

      await invalidatePageQueries(
        queryClient,
        serviceDescriptionKeysStore.serviceDescriptionPage()
      );
      // Invalidate list queries
      queryClient.invalidateQueries({queryKey: serviceDescriptionKeysStore.root});

      // Deleting the last online-bookable service changes the RDV activation
      // prerequisite (onlineService, Story 5.1, #607) just like create/update:
      // refresh the activation status so the switch/missing list reflects it
      // without waiting for the staleTime.
      queryClient.invalidateQueries({queryKey: rdvActivationKeysStore.status()});
    },
    onError(error) {
      console.error("Failed to delete service description:", error);
    },
  });
}

/**
 * Function to get current user service descriptions (for use outside React components)
 * 🔧 Can be used in utility functions, services, etc.
 */
export async function getCurrentUserServiceDescriptions() {
  return serviceDescriptionApiService.getCurrentUserServiceDescriptions();
}
